import { Group, Text, Box, Grid, Flex, Button, Badge, Modal, TextInput, Checkbox, Stack, ActionIcon, Alert } from '@mantine/core';
import { useState, useEffect } from 'react';
import { useDisclosure } from '@mantine/hooks';
import { modals } from '@mantine/modals';
import { notifications } from '@mantine/notifications';
import { IconX, IconCheck, IconTrash, IconRobot, IconPencil } from '@tabler/icons-react';
import { useNavigate } from "react-router-dom";
import { VisiLoader, HelpTip } from "../../GeneralComponent/GeneralComponent";
import SettingsApi from "../../../api/SettingsApi";

const scopeLabels = {
  'campaigns:read': 'Campaigns',
  'tests:read': 'Tests',
  'journeys:read': 'Customer Journeys'
};

const scopeLabel = (scope:string) => scopeLabels[scope] ? scopeLabels[scope] : scope;

const formatDate = (value:string) => {
  if(!value){
    return '';
  }
  const parsed = new Date(value.replace(' ','T'));
  if(isNaN(parsed.getTime())){
    return value;
  }
  return parsed.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
};

type Connection = {
  connection_id: string;
  name: string;
  scopes: string[];
  used_by: string;
  created_at: string;
  last_used_at: string;
}

export function AIConnections() {

  const navigate = useNavigate();

  const [aiConnectionLoader, setAIConnectionLoader] = useState(false);
  const [connections, setConnections] = useState<Connection[]>([]);
  const [availableScopes, setAvailableScopes] = useState<string[]>([]);

  const [createOpened, { open: openCreate, close: closeCreate }] = useDisclosure(false);
  const [editOpened, { open: openEdit, close: closeEdit }] = useDisclosure(false);
  const [editingId, setEditingId] = useState('');
  const [connectionName, setConnectionName] = useState('');
  const [selectedScopes, setSelectedScopes] = useState<string[]>([]);
  const [created, setCreated] = useState(false);

  useEffect(() => {
    getConnectionList();
  }, []);

  const getConnectionList = () => {
    setAIConnectionLoader(true);
    SettingsApi.getaiconnectionlist()
    .then(response => {
      if (response.ok) {
        return response.json();
      } else {
        if (response.status === 401 || response.status === 400) {
          setAIConnectionLoader(false);
          navigate("/logout");
        }
      }
    })
    .then(data => {
      setAIConnectionLoader(false);
      if(data && data.status === 1){
        setConnections(data.data.connection_list);
        setAvailableScopes(data.data.available_scopes);
      }
    })
    .catch(error => {
      console.log('error', error);
      setAIConnectionLoader(false);
    });
  };

  const openCreateModal = () => {
    setConnectionName('');
    setSelectedScopes(availableScopes);
    setCreated(false);
    openCreate();
  };

  const openEditModal = (connection:Connection) => {
    setEditingId(connection.connection_id);
    setConnectionName(connection.name);
    setSelectedScopes(connection.scopes);
    openEdit();
  };

  // renames and re-scopes only - the token is untouched, so the assistant keeps working
  const handleUpdate = () => {
    if(connectionName.trim() === ''){
      notifications.show({ color: 'red', icon: <IconX size="1.1rem" />, title: 'Error', message: 'Name this connection so you can recognise it later.' });
      return;
    }
    if(selectedScopes.length === 0){
      notifications.show({ color: 'red', icon: <IconX size="1.1rem" />, title: 'Error', message: 'Select at least one permission.' });
      return;
    }
    setAIConnectionLoader(true);
    SettingsApi.updateaiconnection({
      connection_id : editingId,
      name : connectionName.trim(),
      scopes : selectedScopes
    })
    .then(response => {
      if (response.ok) {
        return response.json();
      } else {
        if (response.status === 401 || response.status === 400) {
          setAIConnectionLoader(false);
          navigate("/logout");
        }
      }
    })
    .then(data => {
      setAIConnectionLoader(false);
      if(data && data.status === 1){
        closeEdit();
        getConnectionList();
        notifications.show({ color: 'green', icon: <IconCheck size="1.1rem" />, title: 'Success', message: data.message });
      }else{
        notifications.show({ color: 'red', icon: <IconX size="1.1rem" />, title: 'Error', message: data ? data.message : 'Could not update the connection.' });
      }
    })
    .catch(error => {
      console.log('error', error);
      setAIConnectionLoader(false);
    });
  };

  const handleScopeChange = (scope:string, checked:boolean) => {
    setSelectedScopes(checked ? [...selectedScopes, scope] : selectedScopes.filter(item => item !== scope));
  };

  const handleCreate = () => {
    if(connectionName.trim() === ''){
      notifications.show({ color: 'red', icon: <IconX size="1.1rem" />, title: 'Error', message: 'Name this connection so you can recognise it later.' });
      return;
    }
    if(selectedScopes.length === 0){
      notifications.show({ color: 'red', icon: <IconX size="1.1rem" />, title: 'Error', message: 'Select at least one permission.' });
      return;
    }
    setAIConnectionLoader(true);
    SettingsApi.createaiconnection({
      name : connectionName.trim(),
      scopes : selectedScopes
    })
    .then(response => {
      if (response.ok) {
        return response.json();
      } else {
        if (response.status === 401 || response.status === 400) {
          setAIConnectionLoader(false);
          navigate("/logout");
        }
      }
    })
    .then(data => {
      setAIConnectionLoader(false);
      if(data && data.status === 1){
        setCreated(true);
        getConnectionList();
      }else{
        notifications.show({ color: 'red', icon: <IconX size="1.1rem" />, title: 'Error', message: data ? data.message : 'Could not create the connection.' });
      }
    })
    .catch(error => {
      console.log('error', error);
      setAIConnectionLoader(false);
    });
  };

  const revokeConnectionConfirm = (connection:Connection) => modals.openConfirmModal({
    trapFocus: false,
    title: 'Please confirm your action',
    children: (
      <Text size="sm">
        Are you sure you want to revoke <b>{connection.name}</b>? That assistant will stop being able to read your data straight away.
      </Text>
    ),
    labels: { confirm: 'Confirm', cancel: 'Cancel' },
    onCancel: () => {},
    onConfirm: () => {
      setAIConnectionLoader(true);
      SettingsApi.revokeaiconnection({ connection_id : connection.connection_id })
      .then(response => {
        if (response.ok) {
          return response.json();
        } else {
          if (response.status === 401 || response.status === 400) {
            setAIConnectionLoader(false);
            navigate("/logout");
          }
        }
      })
      .then(data => {
        setAIConnectionLoader(false);
        if(data && data.status === 1){
          setConnections(connections.filter(item => item.connection_id !== connection.connection_id));
          notifications.show({ color: 'green', icon: <IconCheck size="1.1rem" />, title: 'Success', message: data.message });
        }else{
          notifications.show({ color: 'red', icon: <IconX size="1.1rem" />, title: 'Error', message: data ? data.message : 'Could not revoke the connection.' });
        }
      })
      .catch(error => {
        console.log('error', error);
        setAIConnectionLoader(false);
      });
    },
  });

  return (
    <>
      <VisiLoader VisiLoaderVisible={aiConnectionLoader} />
      <Group position="apart" mb="xs">
        <Text weight={500}>
          AI CONNECTIONS <HelpTip helpId={385} />
        </Text>
        <Button size="sm" onClick={openCreateModal}>Create Connection</Button>
      </Group>

      <Text size="sm" c="dimmed" mb={15}>
        AI assistants that can read your Visiopt data. Each assistant gets its own connection,
        so you can revoke one without affecting the others. A connection can only read &ndash; it
        can never change anything in your account.
      </Text>

      {connections.length === 0 ? (
        <Group position="center" mt={40} mb={40}>
          <Stack align="center" spacing={5}>
            <IconRobot size="2.5rem" stroke={1.2} color="#adb5bd" />
            <Text size="sm" c="dimmed">No AI assistant is connected yet.</Text>
            <Text size="xs" c="dimmed">Connect one from your assistant, or create a connection here.</Text>
          </Stack>
        </Group>
      ) : (
        <>
          <Grid gutter="xs" pl={10} pr={10} mb={5}>
            <Grid.Col span={3}><Text size="xs" c="dimmed" fw={600} tt="uppercase">Assistant</Text></Grid.Col>
            <Grid.Col span={3}><Text size="xs" c="dimmed" fw={600} tt="uppercase">In use by</Text></Grid.Col>
            <Grid.Col span={3}><Text size="xs" c="dimmed" fw={600} tt="uppercase">Permissions</Text></Grid.Col>
            <Grid.Col span={1}><Text size="xs" c="dimmed" fw={600} tt="uppercase">Last used</Text></Grid.Col>
            <Grid.Col span={2}></Grid.Col>
          </Grid>
          {connections.map(connection => (
            <Box
              key={connection.connection_id}
              mt={10}
              sx={(theme) => ({
                backgroundColor: theme.colorScheme === 'dark' ? theme.colors.dark[5] : '#f8f9fa',
                padding: "10px",
                borderRadius: theme.radius.md,
                border: "0.0625rem solid transparent",
                borderColor: theme.colorScheme === 'dark' ? theme.colors.dark[1] : '#dee2e6'
              })}
            >
              <Grid gutter="xs" align="center">
                <Grid.Col span={3}>
                  <Text size="sm" fw={500}>{connection.name}</Text>
                  <Text size="xs" c="dimmed">Created {formatDate(connection.created_at)}</Text>
                </Grid.Col>
                <Grid.Col span={3}>
                  {connection.used_by ? (
                    <Badge color="teal" variant="light" size="sm" styles={{ inner: { textTransform: 'none' } }}>
                      {connection.used_by}
                    </Badge>
                  ) : (
                    <Text size="xs" c="dimmed">Not connected yet</Text>
                  )}
                </Grid.Col>
                <Grid.Col span={3}>
                  <Group spacing={5}>
                    {connection.scopes.map(scope => (
                      <Badge key={scope} color="blue" variant="light" size="md" styles={{ inner: { textTransform: 'none' } }}>
                        {scopeLabel(scope)}
                      </Badge>
                    ))}
                  </Group>
                </Grid.Col>
                <Grid.Col span={1}>
                  {connection.last_used_at
                    ? <Text size="sm">{formatDate(connection.last_used_at)}</Text>
                    : <Text size="sm" c="dimmed">Never</Text>}
                </Grid.Col>
                <Grid.Col span={2}>
                  <Flex justify="flex-end" align="center" gap={5}>
                    <ActionIcon
                      variant="default"
                      size="lg"
                      title="Edit name and permissions"
                      onClick={() => openEditModal(connection)}
                    >
                      <IconPencil size="1.1rem" />
                    </ActionIcon>
                    <ActionIcon
                      variant="default"
                      size="lg"
                      color="red"
                      title="Revoke this connection"
                      onClick={() => revokeConnectionConfirm(connection)}
                    >
                      <IconTrash size="1.1rem" color="red" />
                    </ActionIcon>
                  </Flex>
                </Grid.Col>
              </Grid>
            </Box>
          ))}
        </>
      )}

      <Modal opened={editOpened} onClose={closeEdit} title="Edit Connection" trapFocus={false} size="lg">
        <Alert color="blue" mb={15}>
          Changing the name or permissions does not change the connection's access token, so the
          assistant using it keeps working. Narrowing permissions takes effect on its next request.
        </Alert>
        <TextInput
          label="Name"
          description="Which assistant is this for?"
          placeholder="Claude Desktop"
          value={connectionName}
          onChange={(event) => setConnectionName(event.currentTarget.value)}
        />
        <Text size="sm" fw={500} mt={15} mb={5}>Permissions</Text>
        <Stack spacing={8}>
          {availableScopes.map(scope => (
            <Checkbox
              key={scope}
              label={scopeLabel(scope)}
              checked={selectedScopes.includes(scope)}
              onChange={(event) => handleScopeChange(scope, event.currentTarget.checked)}
            />
          ))}
        </Stack>
        <Group position="right" mt={20}>
          <Button variant="default" onClick={closeEdit}>Cancel</Button>
          <Button onClick={handleUpdate}>Save</Button>
        </Group>
      </Modal>

      <Modal opened={createOpened} onClose={closeCreate} title={created ? 'Connection created' : 'Create Connection'} trapFocus={false} size="lg">
        {!created ? (
          <>
            <TextInput
              label="Name"
              description="Which assistant is this for?"
              placeholder="Claude Desktop"
              value={connectionName}
              onChange={(event) => setConnectionName(event.currentTarget.value)}
            />
            <Text size="sm" fw={500} mt={15} mb={5}>Permissions</Text>
            <Stack spacing={8}>
              {availableScopes.map(scope => (
                <Checkbox
                  key={scope}
                  label={scopeLabel(scope)}
                  checked={selectedScopes.includes(scope)}
                  onChange={(event) => handleScopeChange(scope, event.currentTarget.checked)}
                />
              ))}
            </Stack>
            <Group position="right" mt={20}>
              <Button variant="default" onClick={closeCreate}>Cancel</Button>
              <Button onClick={handleCreate}>Create</Button>
            </Group>
          </>
        ) : (
          <>
            <Alert icon={<IconCheck size="1rem" />} color="green" mb={10}>
              <b>{connectionName}</b> is ready. Connect this assistant to Visiopt and choose
              it on the connect screen &ndash; Visiopt hands it the access it needs, so there
              is nothing for you to copy.
            </Alert>
            <Group position="right" mt={20}>
              <Button onClick={closeCreate}>Done</Button>
            </Group>
          </>
        )}
      </Modal>
    </>
  );
}
